import CreateApi from '../../shared/Settings/appSettings';
import { localDateStringToUTC, utcToLocalDateString } from "@moment-golf/shared";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Typography,
    IconButton,
    Button,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { StyledTextboxInput } from '../../shared/Components/StyledTextboxInput';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'react-oidc-context';
import { useTBContext } from '../Contexts/useTBStates';
import type { TbEventWithId } from '@moment-golf/types';

function toDateInputValue(d: Date | string): string {
    return utcToLocalDateString(d);
}

interface CopyEventDialogProps {
    open: boolean;
    onClose: () => void;
    sourceEvent: TbEventWithId | null;
    setQueryEventData?: React.Dispatch<React.SetStateAction<boolean>>;
    /** When provided, the new event is associated with this group and onCreated is called with the created event. */
    eventGroupID?: string | null;
    onCreated?: (newEvent: TbEventWithId) => void;
}

export const CopyEventDialog = ({
    open,
    onClose,
    sourceEvent,
    setQueryEventData,
    eventGroupID,
    onCreated,
}: CopyEventDialogProps) => {
    const auth = useAuth();
    const api = CreateApi();
    const { factor, appTheme, userData } = useTBContext();

    const [eventName, setEventName] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open && sourceEvent) {
            setEventName(`Copy of ${sourceEvent.eventName}`);
            const today = toDateInputValue(new Date());
            setStartDate(today);
            setEndDate(today);
        }
    }, [open, sourceEvent]);

    const handleClose = () => {
        if (!submitting) onClose();
    };

    const handleCreateCopy = async () => {
        if (!sourceEvent || !eventName.trim() || !startDate || !endDate) {
            window.alert('Please fill in Event Name, Start Date, and End Date.');
            return;
        }
        const start = new Date(startDate);
        const end = new Date(endDate);
        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
            window.alert('Please enter valid dates.');
            return;
        }
        if (end < start) {
            window.alert('End date must be on or after start date.');
            return;
        }

        setSubmitting(true);
        try {
            const startDateUTC = localDateStringToUTC(startDate);
            const endDateUTC = localDateStringToUTC(endDate);
            const year = new Date(startDateUTC).getFullYear();

            const rounds = (sourceEvent.rounds || []).map((round: any) => {
                const [y, m, d] = startDate.split('-').map(Number);
                const roundDateLocal = new Date(y, m - 1, d);
                roundDateLocal.setDate(roundDateLocal.getDate() + (Number(round.roundNumber) || 1) - 1);
                const roundDateUTC = roundDateLocal.toISOString();
                return {
                    ...round,
                    roundDate: roundDateUTC,
                };
            });

            const payload: any = {
                ...sourceEvent,
                _id: undefined,
                eventName: eventName.trim(),
                startDate: startDateUTC,
                endDate: endDateUTC,
                year,
                published: false,
                canceled: false,
                canceledBy: null,
                canceledAt: null,
                userID: auth.user?.profile.sub,
                ownerName: userData?.displayName ?? '',
                rounds,
                regCloseDate: startDateUTC,
                regOpenDate: null,
                earlyRegCutoffDate: sourceEvent.earlyRegCutoffDate != null ? startDateUTC : null,
                regCutoffDate: sourceEvent.regCutoffDate != null ? startDateUTC : null,
            };
            delete payload._id;
            if (eventGroupID != null) payload.eventGroupIDs = [eventGroupID];

            const res = await api.post<TbEventWithId>('/tb/AddNewTournament', payload);
            if (onCreated && res.data) onCreated(res.data);
            if (setQueryEventData) setQueryEventData((prev) => !prev);
            handleClose();
        } catch (error) {
            console.error('Error copying event:', error);
            window.alert('Failed to create copy. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    minWidth: 400,
                },
            }}
        >
            <DialogTitle sx={{ borderBottom: `1px dotted ${appTheme.palette.primary.light}`, padding: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">Copy Event</Typography>
                    <IconButton size="small" onClick={handleClose} color="error" disabled={submitting}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ padding: 2 }}>
                {sourceEvent && (
                    <Stack direction="column" spacing={2} sx={{ pt: 1 }}>
                        <StyledTextboxInput
                            fullWidth
                            label="Event Name"
                            variant="outlined"
                            appTheme={appTheme}
                            value={eventName}
                            onChange={(e) => setEventName(e.target.value)}
                            error={!eventName.trim()}
                        />
                        <StyledTextboxInput
                            fullWidth
                            label="Start Date"
                            type="date"
                            variant="outlined"
                            appTheme={appTheme}
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            error={!startDate}
                        />
                        <StyledTextboxInput
                            fullWidth
                            label="End Date"
                            type="date"
                            variant="outlined"
                            appTheme={appTheme}
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            error={!endDate}
                        />
                    </Stack>
                )}
            </DialogContent>
            <DialogActions sx={{ padding: 2, borderTop: `1px solid ${appTheme.palette.primary.extraLight}` }}>
                <Button onClick={handleClose} disabled={submitting}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleCreateCopy} disabled={submitting || !sourceEvent}>
                    {submitting ? 'Creating…' : 'Create copy'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
